import React from "react";
import { usePermission } from "../../hooks/usePermission";
import { PERMISSIONS } from "../../config/permissions";
import AgentTenderCard from "../agent-tender/AgentTenderCard";
import { ContactedMarker } from "./v2/cnPills";
import { formatAbsoluteDate } from "../../utils/searchRequestFormat";

// =====================================================================
// Club-facing anonymous request card — Redesign 2.0 (CS2 / N2-1 / M1).
//
// SPEC-EXTRACT §9 (RequestCard RESOLUTION, binding): the club feed renders
// the SAME card as the agent feed — the shipped V2 `AgentTenderCard`,
// byte-identical by design — plus the club-side deltas:
//   A. contacted state → CS1 contacted-marker row as the card's last row
//      (+40px), wired to the EXISTING batch contacted/ side-channel data
//      (you_contacted / contacted_by / contacted_at, DoD 27). Per Marin
//      ruling D-MARIN-1 (6) the marker renders IN ADDITION to the CTA
//      (the artboard-permitted +40px variant) — the CTA is never swapped
//      away on contact; it keeps the existing handler/chat logic (frozen-
//      thread timer/revive included — nothing about chat changes here).
//   D. unverified shows NOTHING on the club side — never a negative pill
//      (v3 principle; lead ruling 2026-07-22). Agent feed unchanged.
//   E. footer CTA = the existing club primary action "Message agent"
//      (contact_agents-gated, hidden without the permission exactly like
//      before); v3 shows no card CTA → flagged OPEN in PARITY-B1.md.
//
// Anonymity invariant (locked): NO club-resolving field is ever shown —
// serializer omits the club; the loupe runs variant="feed" which hard-nulls
// the club client-side. This wrapper adds no identity surface.
// Functional wiring is unchanged: onMessage / onOpenChat / onToggleSave are
// the same handlers the old card called (feed handlers #36-#40).
// =====================================================================

const ClubRequestCard = ({ request, onMessage, onOpenChat, onToggleSave }) => {
  const { you_contacted, contacted_by, contacted_at } = request;
  // Contacting an agent is a ClubRole permission (contact_agents) — without
  // it the button is hidden on the card (the detail page shows the note).
  const canContactAgents = usePermission(PERMISSIONS.CONTACT_AGENTS);

  const messageButton = (extraClass = "") =>
    canContactAgents && onMessage ? (
      <button
        type="button"
        onClick={() => onMessage(request)}
        className={`flex-none bg-[#2283FF] text-white text-[12.5px] font-semibold px-3.5 py-2 rounded-[9px] hover:bg-[#1B6FE0] ${extraClass}`}
      >
        Message agent
      </button>
    ) : null;

  return (
    <AgentTenderCard
      tender={request}
      onToggleSave={onToggleSave}
      hideUnverifiedPill
      // D-MARIN-1 (6): the CTA stays in its slot even when contacted — the
      // marker (with its own Open chat →) rides along as the last row.
      // Same contact_agents gate, same onMessage handler as before.
      cta={messageButton()}
      lastRow={
        you_contacted ? (
          <ContactedMarker
            onOpenChat={onOpenChat ? () => onOpenChat(request) : undefined}
          >
            {/* Existing attribution (DoD 27) wins when known; the artboard
                string is the graceful fallback (flag-only contact). */}
            {contacted_by?.name
              ? `Contacted by ${contacted_by.name}${
                  contacted_at ? ` · ${formatAbsoluteDate(contacted_at)}` : ""
                }`
              : "You contacted this agent"}
          </ContactedMarker>
        ) : null
      }
      previewPrimaryAction={
        !you_contacted && canContactAgents && onMessage
          ? (closePreview) => (
              <button
                type="button"
                onClick={() => {
                  closePreview();
                  onMessage(request);
                }}
                className="w-full py-[11px] bg-[#2283FF] text-white text-[13.5px] font-semibold rounded-[10px] hover:bg-[#1B6FE0]"
              >
                Message agent
              </button>
            )
          : null
      }
    />
  );
};

export default ClubRequestCard;
